import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Jaw } from '@libs/ui-lib';

import { PerioTooltipToothQuadrant } from '../perio-position-indicator-quadrant/perio-position-indicator-quadrant.interface';
import { PerioTooltipToothSurface } from '../perio-exam-toolbar/perio-exam-toolbar.interface';
import { PerioTooltipToothSide } from '../perio-position-indicator-side/perio-position-indicator-side.interface';
import { PerioPositionIndicatorSideComponent } from '../perio-position-indicator-side/perio-position-indicator-side.component';
import { QUADRANT_DEFS, QuadrantId, SIDE_ID_DEFS, SideId } from './perio-position-indicator-constants';

@Component({
  selector: 'lib-perio-position-indicator',
  imports: [CommonModule, PerioPositionIndicatorSideComponent],
  templateUrl: './perio-position-indicator.component.html',
  styleUrl: './perio-position-indicator.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PerioPositionIndicatorComponent {
  public toothList = input.required<PerioTooltipToothSurface[]>();
  public toothNavigate = output<PerioTooltipToothSurface>();

  protected getJaw(quadrantId: QuadrantId): Jaw {
    return quadrantId.split('-')[0] as Jaw;
  }

  protected quadrantList = computed(() => {
    const quadrantMap = new Map<string, PerioTooltipToothQuadrant>();

    for (const tooth of this.toothList()) {
      const id = this.assignQuadrantId(tooth);

      if (!id) continue; // Skip teeth that do not belong to any quadrant

      const quadrant = quadrantMap.get(id) ?? { id, teeth: { buccal: [], lingual: [] } };
      quadrant.teeth[tooth.side].push(tooth);
      quadrantMap.set(id, quadrant);
    }

    return QUADRANT_DEFS.map(({ id }) => {
      return quadrantMap.get(id) ?? { id, teeth: { buccal: [], lingual: [] } };
    });
  });

  protected sideList = computed(() => {
    const sideMap = new Map<SideId, PerioTooltipToothSide>();

    for (const tooth of this.toothList()) {
      const id = this.assignSideId(tooth);

      if (!id) continue;

      const side = sideMap.get(id) ?? { id, jaw: tooth.jaw, teeth: [] };
      side.teeth.push(tooth);
      sideMap.set(id, side);
    }

    return SIDE_ID_DEFS.map(({ id, jaw }): PerioTooltipToothSide => {
      return sideMap.get(id) ?? ({ id, jaw, teeth: [] } as PerioTooltipToothSide);
    });
  });

  private assignQuadrantId(tooth: PerioTooltipToothSurface): QuadrantId | undefined {
    return QUADRANT_DEFS.find(({ range: [start, end] }) => tooth.toothNumber >= start && tooth.toothNumber <= end)?.id;
  }

  private assignSideId(tooth: PerioTooltipToothSurface): SideId | undefined {
    return SIDE_ID_DEFS.find(({ jaw, side }) => tooth.jaw === jaw && tooth.side === side)?.id;
  }

  protected onToothClick(tooth: PerioTooltipToothSurface): void {
    this.toothNavigate.emit(tooth);
  }

  private sortByToothNumber(teeth: PerioTooltipToothSurface[]): void {
    teeth.sort((a, b) => a.toothNumber - b.toothNumber);
  }
}
